import { all, call, put, race, take, takeLatest, takeEvery, cancelled } from 'redux-saga/effects';
import {
  OVERPAYMENT_DATA_REQUEST,
  OVERPAYMENT_GRAPH_DATA_REQUEST,
  OVERPAYMENT_FINANCIAL_PERFORMANCE_GRAPH_DATA_REQUEST,
  PAYMENTS_CLAIMS_DOP_CANCEL,
  OVERPAYMENT_DATA_CANCEL,
  OVERPAYMENT_GRAPH_DATA_CANCEL,
  OVERPAYMENT_FINANCIAL_PERFORMANCE_GRAPH_DATA_CANCEL,
  OVERPAYMENT_SUBCATEGORY_REQUEST,
} from '../../../actions/actionTypes';
import {
  getOverPaymentCardData,
  getOverPaymentCardDataPayment,
  getOverPaymentGraphData,
  getOverPaymentFinancialPerformance,
  getOverPaymentBulkGraphData,
  getOverPaymentBulkCardData,
  getOverPaymentBulkCardDataPayment,
  getOverPaymentBulkFinancialPerformance,
} from '@src/middleware/services/reimbusement/overPayment/overPayment.service';

import {
  fetchOPGraphDataError,
  fetchOPGraphDataSuccess,
  fetchOverPaymentDataError,
  fetchOverPaymentDataSuccess,
  graphDataLoading,
  overpaymentDataLoading,
  fetchOPGraphDataRequest,
  fetchOverPaymentData,
  fetchOverPaymentFinancialPerformanceGraphLoading,
  fetchOverPaymentFinancialPerformanceGraphSuccess,
  fetchOverPaymentFinancialPerformanceGraphError,
  overPaymentPollingStarted,
  overPaymentPollingCompleted,
  overPaymentPollingFailed,
  fetchOverPaymentSubCategorySuccess,
  fetchOverPaymentSubCategoryFailure,
  fetchOverPaymentSubCategoryLoading,
} from '@src/middleware/actions/Reimbursement/overPaymentActions';
import { reimbursementPollingStarted, reimbursementPollingCompleted } from '@src/middleware/actions/Reimbursement/reiumbursementTabDataAction';

export function* fetchOverPaymentgraphData(action: any) {
  const highVolumeTinFlag = sessionStorage.getItem('HIVOLUMETINFLOW') === 'true'
    || sessionStorage.getItem('hiVolumeContractFlow') === 'true';
  const key = highVolumeTinFlag ? 'bulk-polling' : undefined;
  let stopPollingFn: (() => void) | undefined;
  const correlationId = `fetchOverPaymentgraphData-${Date.now()}`;
  const reimbusementPollingArr = JSON.parse(sessionStorage.getItem('reimbursementPolling') || '[]');
  try {
    if (key) {
      yield put(overPaymentPollingStarted());
      if (!reimbusementPollingArr.some(x => x.includes('fetchOverPaymentgraphData-'))) {
        yield put(reimbursementPollingStarted(correlationId));
      }
    }

    yield put(graphDataLoading(true));

    // Race between API calls and cancellation
    const { result, cancel } = yield race({
      result: call(function* () {
        // Only call the initial API (without Reason) to get top categories
        let initialResponse;

        if (key) {
          // Bulk flow - pass options with onStop
          initialResponse = yield call(
            getOverPaymentBulkGraphData,
            action?.payload,
            {
              onStop: (stopFn: () => void) => {
                stopPollingFn = stopFn;
              },
            }
          );
        } else {
          // Normal flow - no options needed
          initialResponse = yield call(
            getOverPaymentGraphData,
            action?.payload
          );
        }

        // Also unwrap the initial response for bulk flow
        const finalInitialResponse = key && initialResponse?.Data?.Result
          ? initialResponse.Data.Result
          : initialResponse;

        let finalres;

        if (key) {
          finalres = {
            Data: finalInitialResponse
          };
        } else {
          finalres = finalInitialResponse;
        }

        // Return only the initial response; subcategories are fetched on-demand
        return { initialResponse: finalres, allResponses: [] };
      }),
      cancel: take(OVERPAYMENT_GRAPH_DATA_CANCEL),
    });

    yield put(graphDataLoading(false));

    if (result) {
      const { initialResponse, allResponses } = result;
      // Combine all responses and dispatch success action
      yield put(fetchOPGraphDataSuccess({ initialResponse, allResponses }));

      if (key) {
        const item = JSON.parse(sessionStorage.getItem('reimbursementPolling') || '[]').find((x: string) => x.includes('fetchOverPaymentgraphData-'));
        if (item) {
          yield put(reimbursementPollingCompleted(item));
        }
      }
    } else {
      yield put(fetchOPGraphDataError('No data received'));
      if (key) {
        yield put(overPaymentPollingFailed());
      }
    }
  } catch (error) {
    yield put(fetchOPGraphDataError(error));
    yield put(graphDataLoading(false));

    if (key) {
      yield put(overPaymentPollingFailed());
    }
  }
}

export function* fetchOverPaymentCardData(action: any) {
  const highVolumeTinFlag = sessionStorage.getItem('HIVOLUMETINFLOW') === 'true'
    || sessionStorage.getItem('hiVolumeContractFlow') === 'true';
  const key = highVolumeTinFlag ? 'bulk-polling' : undefined;
  let stopPollingFn: (() => void) | undefined;
  const correlationId = `fetchOverPaymentCardData-${Date.now()}`;
  try {
    if (key) {
      yield put(reimbursementPollingStarted(correlationId));
    }

    yield put(overpaymentDataLoading(true));

    // Race between API calls and cancellation
    const { result, cancel } = yield race({
      result: call(function* () {
        let initialResponse, paymentMetricResponse;

        if (key) {
          // Bulk flow - pass options with onStop
          [initialResponse, paymentMetricResponse] = yield all([
            call(
              getOverPaymentBulkCardData,
              action?.payload,
              {
                onStop: (stopFn: () => void) => {
                  stopPollingFn = stopFn;
                },
              }
            ),
            call(
              getOverPaymentBulkCardDataPayment,
              action?.payload,
              {
                onStop: (stopFn: () => void) => {
                  stopPollingFn = stopFn;
                },
              }
            ),
          ]);
        } else {
          // Normal flow - no options needed
          [initialResponse, paymentMetricResponse] = yield all([
            call(getOverPaymentCardData, action?.payload),
            call(getOverPaymentCardDataPayment, action?.payload),
          ]);
        }

        // Transform bulk responses to match normal flow structure
        if (key) {
          // Bulk responses from polling: { Status: "OK", Data: { Result: {...actual data...} } }
          // Need to unwrap Result and wrap in full Status structure to match normal flow
          const unwrappedInitial = initialResponse?.Data?.Result || initialResponse;
          const unwrappedPayment = paymentMetricResponse?.Data?.Result || paymentMetricResponse;

          initialResponse = {
            Status: "OK",
            StatusCode: 200,
            Message: "Successfully Data Retrieved",
            TimeStamp: new Date().toISOString(),
            Data: unwrappedInitial
          };

          paymentMetricResponse = {
            Status: "OK",
            StatusCode: 200,
            Message: "Successfully Data Retrieved",
            TimeStamp: new Date().toISOString(),
            Data: unwrappedPayment
          };
        }

        return { initialResponse, paymentMetricResponse };
      }),
      cancel: take(OVERPAYMENT_DATA_CANCEL),
    });

    if (cancel) {
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(overpaymentDataLoading(false));
      if (key) {
        yield put(reimbursementPollingCompleted(correlationId));
      }
      return;
    }

    const { initialResponse, paymentMetricResponse } = result;

    yield put(overpaymentDataLoading(false));

    // Check if responses are valid
    const responseValid = initialResponse && typeof initialResponse === 'object';
    const paymentResponseValid = paymentMetricResponse && typeof paymentMetricResponse === 'object';

    if (!responseValid || !paymentResponseValid) {
      yield put(fetchOverPaymentDataError('Failed to fetch data'));
      if (key) {
        yield put(overPaymentPollingFailed());
      }
    } else {
      // Handle response structure - now both normal and bulk have same structure
      // Both: { Status: "OK", Data: {...} }
      const initialData = initialResponse?.Data;
      const claimsLobSummary = paymentMetricResponse?.Data?.[0]?.All;

      yield put(
        fetchOverPaymentDataSuccess({
          initialData,
          claimsLobSummary,
        })
      );
    }
  } catch (error) {
    yield put(fetchOverPaymentDataError(error));
    yield put(overpaymentDataLoading(false));

    if (key) {
      yield put(overPaymentPollingFailed());
    }
  } finally {
    if (yield cancelled()) {
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(overpaymentDataLoading(false));
      if (key) {
        yield put(reimbursementPollingCompleted(correlationId));
      }
    }
    if (key) {
      yield put(reimbursementPollingCompleted(correlationId));
    }
  }
}

  export function* fetchOverPaymentFinancialPerformance(action: any) {
    const highVolumeTinFlag = sessionStorage.getItem('HIVOLUMETINFLOW') === 'true'
      || sessionStorage.getItem('hiVolumeContractFlow') === 'true';
    const key = highVolumeTinFlag ? 'bulk-polling' : undefined;
    let stopPollingFn: (() => void) | undefined;
    const correlationId = `fetchOverPaymentFinancialPerformance-${Date.now()}`;
    const LobData: { Lob: string }[] = [
      { Lob: 'All' },
      { Lob: 'EI' },
      { Lob: 'MR' },
      { Lob: 'CS' },
      { Lob: 'EXC' },
    ];

    try {
      if (key) {
        yield put(reimbursementPollingStarted(correlationId));
      }

      // Set loading states at the beginning
      yield put(fetchOverPaymentFinancialPerformanceGraphLoading(true));

      // Race between API calls and cancellation
      const { result, cancel } = yield race({
        result: call(function* () {
          let response;

          if (key) {
            // Bulk flow - pass options with onStop
            response = yield call(
              getOverPaymentBulkFinancialPerformance,
              action?.payload,
              {
                onStop: (stopFn: () => void) => {
                  stopPollingFn = stopFn;
                },
              }
            );
          } else {
            // Normal flow - no options needed
            response = yield call(
              getOverPaymentFinancialPerformance,
              action?.payload
            );
          }

          // Transform bulk response to match normal flow structure
          if (key) {
            // Bulk response from polling: { Status: "OK", Data: { Result: {...actual data...} } }
            // Need to unwrap Result and wrap in full Status structure to match normal flow
            const unwrappedData = response?.Data?.Result || response;
            response = {
              Status: "OK",
              StatusCode: 200,
              Message: "Successfully Data Retrieved",
              TimeStamp: new Date().toISOString(),
              Data: unwrappedData
            };
          }

          return response;
        }),
        cancel: take(OVERPAYMENT_FINANCIAL_PERFORMANCE_GRAPH_DATA_CANCEL),
      });

      if (cancel) {
        if (typeof stopPollingFn === 'function') {
          stopPollingFn();
        }
        yield put(fetchOverPaymentFinancialPerformanceGraphLoading(false));
        if (key) {
          yield put(reimbursementPollingCompleted());
        }
        return;
      }

      const response = result;

      // Check if response is valid - now both flows have same structure
      const isValid = response && response?.Status === 'OK' && response?.Data;

      if (!isValid) {
        yield put(
          fetchOverPaymentFinancialPerformanceGraphError(
            'Failed to fetch graph data'
          )
        );
        if (key) {
          yield put(overPaymentPollingFailed());
        }
      } else {
        // Extract data - now both flows have same structure: { Status: "OK", Data: {...} }
        const responseData = response?.Data;

        const graphResponses: any[] = [];

        // Add the response for each LOB in LobData
        for (const lobItem of LobData) {
          graphResponses.push({
            lob: lobItem.Lob,
            data: responseData,
          });
        }

        const combileResponse = {
          initialReponse: key ? response : response,
          graphResponses: graphResponses,
        };

        yield put(
          fetchOverPaymentFinancialPerformanceGraphSuccess(combileResponse)
        );

        if (key) {
          yield put(reimbursementPollingCompleted(correlationId));
        }
      }

      // Reset loading states at the end
      yield put(fetchOverPaymentFinancialPerformanceGraphLoading(false));

    } catch (error) {
      // Handle any errors that occurred during the process
      yield put(fetchOverPaymentFinancialPerformanceGraphLoading(false));
      yield put(
        fetchOverPaymentFinancialPerformanceGraphError(
          'An error occurred while fetching graph data'
        )
      );

      if (key) {
        yield put(overPaymentPollingFailed());
      }
    } finally {
      if (yield cancelled()) {
        if (typeof stopPollingFn === 'function') {
          stopPollingFn();
        }
        yield put(fetchOverPaymentFinancialPerformanceGraphLoading(false));
        if (key) {
          yield put(reimbursementPollingCompleted(correlationId));
        }
      }
    }
  }

  export function* fetchOverPaymentSubCategory(action: any) {
    const { reasonName, apiPayload } = action.payload;
    const highVolumeTinFlag = sessionStorage.getItem('HIVOLUMETINFLOW') === 'true';
    const key = highVolumeTinFlag ? 'bulk-polling' : undefined;

    try {
      yield put(fetchOverPaymentSubCategoryLoading({ reasonName, loading: true }));

      let response: any;

      if (key) {
        const rawResponse = yield call(getOverPaymentBulkGraphData, apiPayload);
        // Bulk service returns the Result content directly ({ PeriodStart, Details });
        // wrap to match normal flow structure { Data: { PeriodStart, Details } }
        response = rawResponse ? { Data: rawResponse } : null;
      } else {
        response = yield call(getOverPaymentGraphData, apiPayload);
      }

      if (response) {
        yield put(fetchOverPaymentSubCategorySuccess({ reasonName, data: response }));
      } else {
        yield put(fetchOverPaymentSubCategoryFailure({ reasonName, error: 'Invalid response' }));
      }
    } catch (error) {
      yield put(fetchOverPaymentSubCategoryFailure({ reasonName, error }));
    } finally {
      yield put(fetchOverPaymentSubCategoryLoading({ reasonName, loading: false }));
    }
  }

  export function* overPaymentSaga() {
    yield takeLatest(OVERPAYMENT_GRAPH_DATA_REQUEST, fetchOverPaymentgraphData);
    yield takeLatest(OVERPAYMENT_DATA_REQUEST, fetchOverPaymentCardData);
    yield takeLatest(
      OVERPAYMENT_FINANCIAL_PERFORMANCE_GRAPH_DATA_REQUEST,
      fetchOverPaymentFinancialPerformance
    );
    // Use takeEvery so multiple reasons can be fetched concurrently
    yield takeEvery(OVERPAYMENT_SUBCATEGORY_REQUEST, fetchOverPaymentSubCategory);
  }